import React, { useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck,
  faChevronDown,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons'
import { PencilSimple, Plus, TrashSimple } from '@phosphor-icons/react'
import type { Competency, SubCompetency } from '../../types/GrowthWheelManagement'
import {
  useCreateEditorSubCompetencyMutation,
  useDeleteEditorCompetencyMutation,
  useReorderEditorCompetenciesOnWheelMutation,
  useUpdateEditorCompetencyMutation,
} from '../../redux/growth-wheel.endpoints'
import { WheelManagementDragHandle } from './WheelManagementDragHandle'
import { SubCompetencyRow } from './SubCompetencyRow'
import { WheelManagementActionsConfirmModal } from './WheelManagementActionsConfirmModal'
import {
  getGwWmActiveDrag,
  getGwWmDragData,
  setGwWmActiveDrag,
  setGwWmDragData,
  setGwWmDragPreviewFromNode,
} from './wheelManagementDnD'
import RichTextEditor from './RichTextEditor'
import RichTextView from './RichTextView'
import type { RootState } from '../../redux/store'

type Props = {
  growthWheelId: number
  competency: Competency
  index: number
  allCompetencies: Competency[]
  allSubCompetencies: SubCompetency[]
  roleName?: string
  competencyDropTargetIndex: number | null
  onCompetencyDropTargetChange: (index: number | null) => void
}

export function WheelManagementCompetencyCard({
  growthWheelId,
  competency,
  index,
  allCompetencies,
  allSubCompetencies,
  roleName,
  competencyDropTargetIndex,
  onCompetencyDropTargetChange,
}: Props) {
  const isReadOnly = useSelector(
    (state: RootState) => state.growthWheel.isReadOnly,
  )
  const cardElRef = useRef<HTMLDivElement>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editingName, setEditingName] = useState('')
  const [editingDesc, setEditingDesc] = useState('')
  const [editingBaseline, setEditingBaseline] = useState<{
    name: string
    description: string
  } | null>(null)

  const [addingSub, setAddingSub] = useState(false)
  const [newSubName, setNewSubName] = useState('')
  const [newSubDesc, setNewSubDesc] = useState('')

  const [deleteModalOpen, setDeleteModalOpen] = useState(false)

  const [isDragging, setIsDragging] = useState(false)
  const [subDropTargetIndex, setSubDropTargetIndex] = useState<number | null>(
    null,
  )

  const [updateCompetency] = useUpdateEditorCompetencyMutation()
  const [deleteCompetency] = useDeleteEditorCompetencyMutation()
  const [createSubCompetency] = useCreateEditorSubCompetencyMutation()
  const [reorderWheel] = useReorderEditorCompetenciesOnWheelMutation()

  const subCompetencies = useMemo(
    () => allSubCompetencies.filter(c => c.competency === competency.id),
    [allSubCompetencies, competency.id],
  )

  const competencyDragEnabled = !isReadOnly && !isEditing && !isExpanded
  const isDropTarget = competencyDropTargetIndex === index && !isDragging

  const editSaveEnabled =
    isEditing &&
    editingBaseline !== null &&
    Boolean(editingName.trim()) &&
    (editingName !== editingBaseline.name ||
      editingDesc !== editingBaseline.description)

  const startEdit = () => {
    if (isReadOnly) return
    setIsEditing(true)
    setEditingName(competency.name)
    setEditingDesc(competency.description)
    setEditingBaseline({
      name: competency.name,
      description: competency.description,
    })
  }

  const cancelEdit = () => {
    setIsEditing(false)
    setEditingName('')
    setEditingDesc('')
    setEditingBaseline(null)
  }

  const saveEdit = async () => {
    if (
      !editingBaseline ||
      !editingName.trim() ||
      (editingName === editingBaseline.name &&
        editingDesc === editingBaseline.description)
    ) {
      return
    }
    await updateCompetency({
      growthWheelId,
      competencyId: Number(competency.id),
      body: {
        name: editingName,
        description: editingDesc,
      },
    }).unwrap()
    setIsEditing(false)
    setEditingName('')
    setEditingDesc('')
    setEditingBaseline(null)
  }

  const persistCompetencyOrder = async (fromIndex: number, toIndex: number) => {
    const next = [...allCompetencies]
    const [item] = next.splice(fromIndex, 1)
    next.splice(toIndex, 0, item)
    await reorderWheel({
      growthWheelId,
      orderedCompetencyIds: next.map(a => Number(a.id)),
    }).unwrap()
  }

  const submitNewSub = async () => {
    if (!newSubName.trim()) return
    await createSubCompetency({
      growthWheelId,
      competencyId: Number(competency.id),
      body: {
        name: newSubName.trim(),
        description: newSubDesc.trim(),
      },
    }).unwrap()
    setNewSubName('')
    setNewSubDesc('')
    setAddingSub(false)
  }

  const confirmDelete = async () => {
    await deleteCompetency({
      growthWheelId,
      competencyId: Number(competency.id),
    }).unwrap()
    setDeleteModalOpen(false)
  }

  const handleCompetencyDragStart = (e: React.DragEvent<HTMLSpanElement>) => {
    if (!competencyDragEnabled) return
    setGwWmDragData(e.dataTransfer, { kind: 'competency', fromIndex: index })
    setGwWmDragPreviewFromNode(
      e.dataTransfer,
      e.clientX,
      e.clientY,
      cardElRef.current,
    )
    setGwWmActiveDrag({ kind: 'competency', fromIndex: index })
    setIsDragging(true)
  }

  const handleCompetencyDragEnd = () => {
    setGwWmActiveDrag(null)
    onCompetencyDropTargetChange(null)
    setIsDragging(false)
  }

  const handleCompetencyDragOverCapture = (e: React.DragEvent) => {
    if (isReadOnly) return
    const active = getGwWmActiveDrag()
    if (active?.kind !== 'competency') return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (active.fromIndex !== index) {
      onCompetencyDropTargetChange(index)
    }
  }

  const handleCompetencyDrop = (e: React.DragEvent) => {
    if (isReadOnly) return
    const p = getGwWmDragData(e)
    if (!p || p.kind !== 'competency') return
    e.preventDefault()
    onCompetencyDropTargetChange(null)
    if (p.fromIndex !== index) {
      void persistCompetencyOrder(p.fromIndex, index)
    }
  }

  const handleExpandBodyDragOver = (e: React.DragEvent) => {
    if (isReadOnly) return
    if (getGwWmActiveDrag()?.kind === 'competency') {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
    }
  }

  return (
    <>
      <div
        ref={cardElRef}
        className={`gw-wm__area-card${isDragging ? ' gw-wm__area-card--dragging' : ''}${isDropTarget ? ' gw-wm__area-card--drop-target' : ''}`}
        aria-dropeffect={isDropTarget ? 'move' : undefined}
        onDragOverCapture={handleCompetencyDragOverCapture}
        onDrop={handleCompetencyDrop}
      >
        <div className="gw-wm__area-header">
          {isEditing ? (
            <div className="gw-wm__form-stack">
              <div>
                <label className="gw-wm__label">Competency name</label>
                <input
                  type="text"
                  className="gw-wm__input"
                  value={editingName}
                  onChange={e => setEditingName(e.target.value)}
                />
              </div>
              <div>
                <label className="gw-wm__label">Description</label>
                <RichTextEditor
                value={editingDesc}
                onChange={val => setEditingDesc(val)}
                placeholder="Describe this competency..."
              />
              </div>
              <div className="gw-wm__btn-row">
                <button
                  type="button"
                  className="gw-wm__btn-primary"
                  draggable={false}
                  disabled={!editSaveEnabled}
                  onClick={() => void saveEdit()}
                >
                  <FontAwesomeIcon icon={faCheck} />
                  Save
                </button>
                <button
                  type="button"
                  className="gw-wm__btn-secondary"
                  draggable={false}
                  onClick={cancelEdit}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="gw-wm__area-header-row">
              <div className="gw-wm__area-header-main">
                <WheelManagementDragHandle
                  enabled={competencyDragEnabled}
                  onDragStart={handleCompetencyDragStart}
                  onDragEnd={handleCompetencyDragEnd}
                  title={
                    competencyDragEnabled
                      ? 'Drag to reorder competency'
                      : 'Collapse this competency to reorder it among others'
                  }
                  className="gw-wm__drag-handle--area"
                />
                <button
                  type="button"
                  className="gw-wm__btn-icon"
                  draggable={false}
                  onClick={() => setIsExpanded(v => !v)}
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                >
                  <FontAwesomeIcon
                    icon={isExpanded ? faChevronDown : faChevronRight}
                  />
                </button>
                <div className="gw-wm__area-header-text">
                  <h3 className="gw-wm__area-title">{competency.name}</h3>
                  <RichTextView
                  html={competency.description}
                  fallback={
                    <p className="gw-wm__area-desc gw-wm__desc-placeholder">
                      No description yet.
                    </p>
                  }
                  />
                  <p className="gw-wm__area-meta">
                    {subCompetencies.length}{' '}
                    {subCompetencies.length === 1
                      ? 'sub-competency'
                      : 'sub-competencies'}
                  </p>
                </div>
              </div>
              {!isReadOnly ? (
                <div className="gw-wm__area-actions">
                  <button
                    type="button"
                    className="gw-wm__btn-icon"
                    draggable={false}
                    title="Edit competency"
                    onClick={startEdit}
                  >
                    <PencilSimple size={16} weight="regular" aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="gw-wm__btn-icon gw-wm__btn-icon--danger"
                    draggable={false}
                    title="Delete competency"
                    onClick={() => setDeleteModalOpen(true)}
                  >
                    <TrashSimple size={16} weight="regular" aria-hidden />
                  </button>
                </div>
              ) : null}
            </div>
          )}
        </div>

        {isExpanded && (
          <div
            className="gw-wm__expand-body"
            onDragOver={handleExpandBodyDragOver}
          >
            {subCompetencies.length === 0 && !addingSub && (
              <div className="gw-wm__sub-empty">
                <p className="gw-wm__hint mb-3">
                  No sub-competencies in this competency yet
                </p>
                {!isReadOnly ? (
                  <button
                    type="button"
                    className="gw-wm__btn-primary"
                    draggable={false}
                    onClick={() => setAddingSub(true)}
                  >
                    Add First Sub-Competency
                  </button>
                ) : null}
              </div>
            )}
            {subCompetencies.length > 0 && (
              <div
                className="gw-wm__sub-list"
                onDragLeave={e => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    setSubDropTargetIndex(null)
                  }
                }}
              >
                {subCompetencies.map((sub, subIndex) => (
                  <SubCompetencyRow
                    key={sub.id}
                    growthWheelId={growthWheelId}
                    subCompetency={sub}
                    competencyId={competency.id}
                    competencyIndex={index}
                    subIndex={subIndex}
                    siblingSubCompetencies={subCompetencies}
                    subDropTargetIndex={subDropTargetIndex}
                    onSubDropTargetChange={setSubDropTargetIndex}
                    roleName={roleName}
                    parentCompetencyName={competency.name}
                    onCompetencyReorderDrag={(from, to) =>
                      void persistCompetencyOrder(from, to)
                    }
                  />
                ))}
              </div>
            )}

            {!isReadOnly && addingSub ? (
              <div className="gw-wm__form-panel gw-wm__form-panel--inset">
                <h4 className="gw-wm__form-panel-title">
                  Add New Sub-Competency
                </h4>
                <div className="gw-wm__form-stack">
                  <input
                    type="text"
                    className="gw-wm__input"
                    value={newSubName}
                    onChange={e => setNewSubName(e.target.value)}
                    placeholder="Sub-competency name"
                  />
                  <RichTextEditor
                    value={newSubDesc}
                    onChange={val => setNewSubDesc(val)}
                    placeholder="Description"
                  />
                  <div className="gw-wm__btn-row">
                    <button
                      type="button"
                      className="gw-wm__btn-primary"
                      draggable={false}
                      disabled={!newSubName.trim()}
                      onClick={() => void submitNewSub()}
                    >
                      <FontAwesomeIcon icon={faCheck} />
                      Add
                    </button>
                    <button
                      type="button"
                      className="gw-wm__btn-secondary"
                      draggable={false}
                      onClick={() => {
                        setAddingSub(false)
                        setNewSubName('')
                        setNewSubDesc('')
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            ) : !isReadOnly && subCompetencies.length > 0 ? (
              <button
                type="button"
                className="gw-wm__add-dashed"
                draggable={false}
                onClick={() => setAddingSub(true)}
              >
                <Plus size={16} weight="regular" aria-hidden />
                Add Sub-Competency
              </button>
            ) : null}
          </div>
        )}
      </div>

      {deleteModalOpen && (
        <WheelManagementActionsConfirmModal
          title="Delete competency"
          confirmLabel="Delete competency"
          onCancel={() => setDeleteModalOpen(false)}
          onConfirm={() => void confirmDelete()}
        >
          <p className="gw-wm__hint">
            Are you sure you want to delete &quot;{competency.name}&quot;? This
            will also delete all {subCompetencies.length} sub-competencies, their
            levels 1–5 rubric definitions, and any associated assessment scores
            and notes.
          </p>
        </WheelManagementActionsConfirmModal>
      )}
    </>
  )
}

/** @deprecated Use WheelManagementCompetencyCard */
export const WheelManagementAreaCard = WheelManagementCompetencyCard
